import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Check, Coins, Link2, Wallet, Users } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "@/i18n/translations";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Footer from "@/components/Footer";
import PartnerCalculator from "@/components/PartnerCalculator";

export default function PartnerLanding() {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    promo_channel: "",
    desired_ref_code: "",
  });
  const [created, setCreated] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/partner/apply`, form, { headers });
      setCreated(data.partner);
      if (data.status === "exists") {
        toast.info(t("partner.toast.exists"));
      } else {
        toast.success(t("partner.toast.created"));
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || t("partner.toast.error");
      toast.error(typeof msg === "string" ? msg : t("partner.toast.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const refUrl = created
    ? `${window.location.origin}/?ref=${created.ref_code}`
    : null;

  const features = [
    { id: "feat-commission", icon: Coins, t: t("partner.feature.commission.title"), d: t("partner.feature.commission.desc") },
    { id: "feat-link", icon: Link2, t: t("partner.feature.link.title"), d: t("partner.feature.link.desc") },
    { id: "feat-dashboard", icon: Users, t: t("partner.feature.dashboard.title"), d: t("partner.feature.dashboard.desc") },
    { id: "feat-payout", icon: Wallet, t: t("partner.feature.payout.title"), d: t("partner.feature.payout.desc") },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Helmet>
        <title>{t("partner.meta.title")}</title>
        <meta name="description" content={t("partner.meta.description")} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-6 pt-10 pb-4 flex justify-between items-center">
        <button
          onClick={() => navigate("/")}
          className="text-2xl font-bold tracking-tight hover:opacity-80"
          data-testid="partner-back-home-btn"
        >
          {t("partner.nav.back")}
        </button>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <button
            onClick={() => navigate("/partner/dashboard")}
            className="text-sm text-zinc-400 hover:text-white"
            data-testid="partner-go-dashboard-btn"
          >
            {t("partner.nav.dashboard")}
          </button>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <div className="inline-flex items-center gap-2 bg-yellow-400/10 text-yellow-300 px-3 py-1 rounded-full text-xs font-medium mb-6">
            <Coins className="w-3.5 h-3.5" /> {t("partner.badge")}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            {t("partner.hero.title1")} <span className="text-yellow-300">30%</span><br/>
            {t("partner.hero.title2")}<br/>
            {t("partner.hero.title3")}
          </h1>
          <p className="mt-6 text-lg text-zinc-400 max-w-lg leading-relaxed">
            {t("partner.hero.subtitle")}
          </p>

          <div className="mt-10 space-y-4">
            {features.map((f) => (
              <div key={f.id} className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                  <f.icon className="w-5 h-5 text-yellow-300" />
                </div>
                <div>
                  <div className="font-medium">{f.t}</div>
                  <div className="text-sm text-zinc-500">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {!created ? (
          <form
            onSubmit={handleSubmit}
            className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 lg:sticky lg:top-10"
            data-testid="partner-apply-form"
          >
            <h2 className="text-lg font-semibold mb-1">{t("partner.form.title")}</h2>
            <p className="text-sm text-zinc-500 mb-6">{t("partner.form.subtitle")}</p>

            <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">
              {t("partner.form.name")}
            </label>
            <Input
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder={t("partner.form.namePlaceholder")}
              className="bg-zinc-900 border-zinc-800 text-white mb-4"
              data-testid="partner-input-name"
            />

            <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">
              {t("partner.form.email")}
            </label>
            <Input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              className="bg-zinc-900 border-zinc-800 text-white mb-4"
              data-testid="partner-input-email"
            />

            <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">
              {t("partner.form.code")}
            </label>
            <Input
              value={form.desired_ref_code}
              onChange={(e) =>
                setForm({
                  ...form,
                  desired_ref_code: e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_-]/g, "")
                    .slice(0, 24),
                })
              }
              placeholder="ivan42"
              className="bg-zinc-900 border-zinc-800 text-white mb-4"
              data-testid="partner-input-refcode"
            />

            <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">
              {t("partner.form.channel")}
            </label>
            <Textarea
              value={form.promo_channel}
              onChange={(e) => setForm({ ...form, promo_channel: e.target.value })}
              placeholder={t("partner.form.channelPlaceholder")}
              className="bg-zinc-900 border-zinc-800 text-white mb-6 min-h-[80px]"
              data-testid="partner-input-channel"
            />

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-yellow-300 text-black hover:bg-yellow-200 font-semibold h-12"
              data-testid="partner-submit-btn"
            >
              {submitting ? t("partner.form.submitting") : t("partner.form.submit")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-zinc-600 mt-4 text-center">
              {t("partner.form.terms")}
            </p>
          </form>
        ) : (
          <div className="bg-zinc-950 border border-yellow-400/20 rounded-2xl p-8 lg:sticky lg:top-10">
            <div className="flex items-center gap-2 text-yellow-300 mb-3">
              <Check className="w-5 h-5" />
              <span className="font-semibold">{t("partner.success.heading")}</span>
            </div>
            <h2 className="text-xl font-semibold mb-6">{t("partner.success.title")}</h2>
            <div className="bg-black/60 border border-zinc-800 rounded-lg p-3 font-mono text-sm break-all mb-4" data-testid="partner-ref-link">
              {refUrl}
            </div>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(refUrl);
                toast.success(t("partner.toast.copied"));
              }}
              className="w-full bg-yellow-300 text-black hover:bg-yellow-200 mb-3"
              data-testid="partner-copy-link-btn"
            >
              {t("partner.success.copy")}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/partner/dashboard")}
              className="w-full border-zinc-700 text-white hover:bg-zinc-900"
              data-testid="partner-open-dashboard-btn"
            >
              {t("partner.success.openDashboard")}
            </Button>
            <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
              {t("partner.success.hint")} <strong>{created.email}</strong>.
            </p>
          </div>
        )}
      </section>

      <PartnerCalculator lang={lang} />

      <Footer />
    </div>
  );
}
